<template>
    <v-dialog :value="value" @input="$emit('input', $event)" max-width="750" overlay-color="#000" :fullscreen="phoneOnly" :hide-overlay="phoneOnly" :transition="phoneOnly ? 'dialog-bottom-transition' : 'dialog-transition'">
        <v-card class="d-flex flex-column" style="height: 100%; max-height: 600px;">
            <v-card-title class="d-flex align-center justify-space-between">
                <span>{{ modeConfig.title }}</span>
                <v-btn icon @click="$emit('input', false)">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="flex-grow-1 pa-0" style="overflow-y: auto;" ref="chatContainer">
                <div class="chat-messages pa-4">
                    <div v-if="chatMessages.length === 0" class="text-center py-8">
                        <v-icon size="48" color="primary" class="mb-4">{{ modeConfig.icon }}</v-icon>
                        <div style="font-size: 1.1rem;" class="grey--text">{{ modeConfig.emptyText }}</div>
                    </div>
                    <div 
                        v-for="(message, index) in chatMessages" 
                        :key="'message-' + index" 
                        :class="['chat-message', message.sender === 'user' ? 'user-message' : 'ai-message']"
                    >
                        <div class="chat-bubble">
                            <div v-if="message.image" class="chat-image-container mb-2">
                                <img :src="getImageSrc(message.image)" alt="question image" class="chat-image" />
                            </div>
                            <div v-if="message.sender === 'user'" class="chat-text">
                                {{ message.text || 'جاوب على الصورة' }}
                            </div>
                            <div v-else class="chat-text ai-response" v-html="formatMessage(message.text)"></div>
                        </div>
                    </div>
                    <div v-if="isTyping" :key="'typing'" class="chat-message ai-message">
                        <div class="chat-bubble">
                            <div class="typing-indicator">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        </div>
                    </div>
                    <div ref="messagesEnd"></div>
                </div>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-text v-if="escalated" class="pa-4 text-center">
                <v-icon size="28" color="primary" class="mb-2">mdi-account-tie-voice-outline</v-icon>
                <div class="details-text muted--text" :class="{'mb-4': canOpenAgent}">تم تحويل هذه المحادثة إلى أحد موظفي الدعم، وسيتم التواصل معك في أقرب وقت.</div>
                <v-btn v-if="canOpenAgent" depressed rounded color="primary" :block="phoneOnly" @click="$emit('open-agent')">
                    <v-icon left>mdi-message-processing-outline</v-icon>
                    <strong>فتح المحادثة مع الدعم</strong>
                </v-btn>
            </v-card-text>
            <v-card-text v-else class="pa-3">
                <div v-if="selectedImage" class="mb-3">
                    <div class="d-flex align-center">
                        <v-img :src="getImagePreviewSrc()" max-width="100" max-height="100" class="rounded-lg mr-2"></v-img>
                        <v-btn icon small @click="removeImage">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </div>
                </div>
                <v-form @submit.prevent="sendMessage">
                    <!-- Mobile layout: textarea full width, buttons below -->
                    <template v-if="phoneOnly">
                        <v-textarea
                            v-model="chatInput"
                            outlined
                            dense
                            rows="2"
                            hide-details
                            :placeholder="modeConfig.placeholder"
                            :disabled="composerDisabled"
                            @keydown.enter.exact.prevent="sendMessage"
                            @keydown.enter.shift.exact="chatInput += '\n'"
                            class="mb-2"
                            style="width: 100%;"
                            no-resize
                        ></v-textarea>
                        <div class="d-flex align-center justify-end">
                            <input
                                ref="fileInput"
                                type="file"
                                accept="image/*"
                                style="display: none;"
                                @change="handleImageSelect"
                            />
                            <v-btn
                                icon
                                large
                                class="me-2"
                                @click="$refs.fileInput.click()"
                                :disabled="composerDisabled"
                            >
                                <v-icon>mdi-image</v-icon>
                            </v-btn>
                            <v-btn
                                icon
                                large
                                class="me-2"
                                @click="showCameraDialog = true"
                                :disabled="composerDisabled"
                            >
                                <v-icon>mdi-camera</v-icon>
                            </v-btn>
                            <v-btn
                                color="primary"
                                :disabled="(!chatInput.trim() && !selectedImage) || composerDisabled"
                                :loading="isTyping"
                                @click="sendMessage"
                                icon
                                large
                            >
                                <v-icon style="transform: rotate(180deg);">mdi-send</v-icon>
                            </v-btn>
                        </div>
                    </template>
                    <!-- Desktop layout: textarea and buttons in horizontal row -->
                    <template v-else>
                        <div class="d-flex align-end">
                            <v-textarea
                                v-model="chatInput"
                                outlined
                                dense
                                rows="2"
                                hide-details
                                :placeholder="modeConfig.placeholder"
                                :disabled="composerDisabled"
                                @keydown.enter.exact.prevent="sendMessage"
                                @keydown.enter.shift.exact="chatInput += '\n'"
                                class="flex-grow-1 me-2"
                                no-resize
                            ></v-textarea>
                            <input
                                ref="fileInput"
                                type="file"
                                accept="image/*"
                                style="display: none;"
                                @change="handleImageSelect"
                            />
                            <v-btn
                                icon
                                large
                                class="me-2"
                                @click="$refs.fileInput.click()"
                                :disabled="composerDisabled"
                            >
                                <v-icon>mdi-image</v-icon>
                            </v-btn>
                            <v-btn
                                icon
                                large
                                class="me-2"
                                @click="showCameraDialog = true"
                                :disabled="composerDisabled"
                            >
                                <v-icon>mdi-camera</v-icon>
                            </v-btn>
                            <v-btn
                                color="primary"
                                :disabled="(!chatInput.trim() && !selectedImage) || composerDisabled"
                                :loading="isTyping"
                                @click="sendMessage"
                                icon
                                large
                            >
                                <v-icon style="transform: rotate(180deg);">mdi-send</v-icon>
                            </v-btn>
                        </div>
                    </template>
                </v-form>
            </v-card-text>
        </v-card>
        
        <!-- Camera Modal -->
        <v-dialog v-model="showCameraDialog" max-width="500" persistent>
            <v-card>
                <v-card-title class="d-flex align-center justify-space-between">
                    <span>التقاط صورة</span>
                    <v-btn icon small @click="closeCamera">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text class="pa-0">
                    <div class="camera-container">
                        <video v-show="!cameraError" ref="videoElement" autoplay playsinline class="camera-video"></video>
                        <div v-if="cameraError" class="pa-4 text-center muted--text">
                            <v-icon color="error" size="48" class="mb-2">mdi-camera-off</v-icon>
                            <div class="mx-8" style="font-size: 1.25rem;">{{ cameraError }}</div>
                        </div>
                    </div>
                </v-card-text>
                <v-card-actions class="d-flex justify-space-between">
                    <v-btn @click="switchCamera" :disabled="!cameraStream">
                        <v-icon left>mdi-camera-flip</v-icon>
                        عكس الكاميرا
                    </v-btn>
                    <div>
                        <v-btn text @click="closeCamera">إلغاء</v-btn>
                        <v-btn color="primary" @click="capturePhoto" :disabled="!cameraStream || cameraError">
                            <v-icon left>mdi-camera</v-icon>
                            التقاط
                        </v-btn>
                    </div>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed'
import GlobalMethods from '@/helpers/global-methods'
import formatAiMessage from '@/helpers/format-ai-message'

export default {
    name: "AiChatDialog",
    props: {
        value: {
            type: Boolean,
            default: false
        },
        // 'bank' = عبد الجواد AI في صفحة أسئلتي، 'support' = الدعم الفني
        mode: {
            type: String,
            default: 'bank'
        },
        // محادثة دعم قديمة نفتحها بدل ما نبدأ واحدة جديدة
        conversation: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            chatMessages: [],
            chatInput: '',
            isTyping: false,
            selectedImage: null,
            showCameraDialog: false,
            cameraStream: null,
            cameraError: null,
            facingMode: 'environment', // 'environment' for back camera, 'user' for front camera
            parentQuestionId: null,

            // support mode only
            conversationId: null,
            escalated: false,
            canOpenAgent: false,
        }
    },
    computed: {
        isSupport(){
            return this.mode === 'support';
        },
        modeConfig(){
            if (this.isSupport){
                return {
                    title: 'الدعم الذكي',
                    icon: 'mdi-face-agent',
                    emptyText: 'أهلاً! اكتب مشكلتك وأنا هساعدك فوراً...',
                    placeholder: 'اكتب مشكلتك هنا...',
                };
            }
            return {
                title: 'اسأل عبد الجواد AI',
                icon: 'mdi-robot-outline',
                emptyText: 'أهلاً! اسأل وأنا جاهز أجاوب على أي سؤال...',
                placeholder: 'اكتب سؤالك هنا...',
            };
        },
        // بعد التحويل للدعم البشري المحادثة بتتقفل
        composerDisabled(){
            return this.isTyping || this.escalated;
        },
        ...GlobalComputed,
    },
        watch: {
        value(val) {
            if (val) {
                if (this.isSupport){
                    this.hydrateConversation();
                }
                // Reset chat when dialog opens
                this.$nextTick(() => {
                    this.scrollToBottom();
                });
            } else {
                // Reset chat state when dialog closes
                this.chatMessages = [];
                this.chatInput = '';
                this.selectedImage = null;
                this.parentQuestionId = null;
                this.conversationId = null;
                this.escalated = false;
                this.canOpenAgent = false;
                this.closeCamera();
            }
        },
        showCameraDialog(val) {
            if (val) {
                this.$nextTick(() => {
                    this.startCamera();
                });
            } else {
                this.stopCamera();
            }
        },
        chatMessages() {
            this.$nextTick(() => {
                this.scrollToBottom();
            });
        },
        isTyping() {
            if (this.isTyping) {
                this.$nextTick(() => {
                    this.scrollToBottom();
                });
            }
        }
    },
    methods: {
        ...GlobalMethods,
        formatMessage(text) {
            return formatAiMessage(text);
        },
        handleImageSelect(event) {
            const file = event.target.files[0];
            if (file) {
                this.selectedImage = file;
            }
        },
        removeImage() {
            this.selectedImage = null;
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
        },
        async startCamera() {
            try {
                this.stopCamera(); // Stop any existing stream
                this.cameraError = null;

                const constraints = {
                    video: { 
                        facingMode: this.facingMode,
                        width: { ideal: 1280 },
                        height: { ideal: 720 }
                    }
                };

                const stream = await navigator.mediaDevices.getUserMedia(constraints);
                this.cameraStream = stream;
                
                if (this.$refs.videoElement) {
                    this.$refs.videoElement.srcObject = stream;
                }
            } catch (error) {
                console.error('Camera error:', error);
                this.cameraError = 'تعذر الوصول إلى الكاميرا. يرجى التحقق من الصلاحيات.';
                this.stopCamera();
            }
        },
        stopCamera() {
            if (this.cameraStream) {
                this.cameraStream.getTracks().forEach(track => track.stop());
                this.cameraStream = null;
            }
            if (this.$refs.videoElement) {
                this.$refs.videoElement.srcObject = null;
            }
        },
        switchCamera() {
            this.facingMode = this.facingMode === 'environment' ? 'user' : 'environment';
            this.startCamera();
        },
        capturePhoto() {
            if (!this.$refs.videoElement || !this.cameraStream) return;

            const video = this.$refs.videoElement;
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0);

            // Convert canvas to blob, then to File
            canvas.toBlob((blob) => {
                if (blob) {
                    const file = new File([blob], 'camera-photo.png', { type: 'image/png' });
                    this.selectedImage = file;
                    this.closeCamera();
                }
            }, 'image/png');
        },
        closeCamera() {
            this.showCameraDialog = false;
            this.stopCamera();
        },
        getImageSrc(image) {
            if (!image) return null;
            if (typeof image === 'string') {
                // If it's a URL or data URL
                if (image.startsWith('data:') || image.startsWith('http')) {
                    return image;
                }
                // If it's a File object, create object URL
                return URL.createObjectURL(image);
            }
            // If it's a File object
            return URL.createObjectURL(image);
        },
        getImagePreviewSrc() {
            if (!this.selectedImage) return null;
            if (this.selectedImage instanceof File) {
                return URL.createObjectURL(this.selectedImage);
            }
            return this.selectedImage;
        },
        sendMessage() {
            if ((!this.chatInput.trim() && !this.selectedImage) || this.composerDisabled) return;

            const questionText = this.chatInput.trim() || 'جاوب على الصورة';
            const imageToSend = this.selectedImage;
            
            // Clear inputs
            this.chatInput = '';
            this.selectedImage = null;
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }

            // Add user message with image preview
            this.chatMessages.push({
                sender: 'user',
                text: questionText,
                image: imageToSend
            });

            // Set typing indicator
            this.isTyping = true;

            // Prepare request data
            let requestData;
            let headers = {};

            // كل وضع بيربط رسائله بمفتاح مختلف
            const threadKey = this.isSupport ? 'conversation_id' : 'parent_question_id';
            const threadId = this.isSupport ? this.conversationId : this.parentQuestionId;

            if (imageToSend) {
                // Use FormData for image upload
                const formData = new FormData();
                formData.append('question', questionText);
                formData.append('image', imageToSend);
                if (threadId) {
                    formData.append(threadKey, threadId);
                }
                requestData = formData;
                headers['Content-Type'] = 'multipart/form-data';
            } else {
                // Regular JSON request
                requestData = {
                    question: questionText
                };
                if (threadId) {
                    requestData[threadKey] = threadId;
                }
            }

            // Send to API
            this.$store.dispatch('fastPostAction', {
                url: this.isSupport ? '/support-chat/ask' : '/ai/ask',
                data: requestData,
                headers: headers
            }).then(response => {
                this.isTyping = false;

                if (this.isSupport){
                    this.handleSupportResponse(response.data);
                    return;
                }

                const { question, answer } = response.data;

                // Set parentQuestionId from first response if not already set
                if (!this.parentQuestionId && response.data.parent) {
                    this.parentQuestionId = response.data.parent.id;
                }
                if (response.data.parent){
                    this.$emit('sync-parent', response.data.parent);
                }

                // Add AI response
                this.chatMessages.push({
                    sender: 'ai',
                    text: answer
                });

            }).catch(error => {
                this.isTyping = false;

                let errorMessage = 'حدثت مشكلة أثناء تنفيذ الطلب';
                
                if (error.response) {
                    if (error.response.status === 429) {
                        errorMessage = error.response.data.error || 'لقد وصلت إلى الحد الأقصى لعدد الأسئلة المسموح بها اليوم';
                    } else if (error.response.status === 503) {
                        errorMessage = error.response.data.error || 'لدينا مشكلة في خدمة عبد الجواد AI، يُرجى المحاولة في وقت لاحق';
                    } else if (error.response.data && error.response.data.error) {
                        errorMessage = error.response.data.error;
                    }
                }

                this.showMsg(errorMessage, 'error');

                // Add error message to chat
                this.chatMessages.push({
                    sender: 'ai',
                    text: '❌ ' + errorMessage
                });
            });
        },
        hydrateConversation() {
            if (!this.conversation) return;

            this.conversationId = this.conversation.id;
            this.escalated = !!this.conversation.escalated;
            // السيرفر هو اللي بيقرر لسه فيه وقت لفتح كريسب ولا لأ
            this.canOpenAgent = !!this.conversation.can_open_agent;
            this.chatMessages = (this.conversation.messages || []).map(message => ({
                sender: message.role === 'ai' ? 'ai' : 'user',
                text: message.text,
                image: message.image
            }));
        },
        handleSupportResponse(data) {
            this.conversationId = data.conversation_id;

            // رد الـ AI نفسه، وده اللي بيتعرض للطالب حتى في حالة التحويل
            this.chatMessages.push({
                sender: 'ai',
                text: data.message ? data.message.text : ''
            });

            // ملخص التحويل agent-facing، مايتعرضش في الشات خالص
            if (data.escalated){
                this.escalated = true;
            }
            this.canOpenAgent = !!data.can_open_agent;

            // نحدّث قائمة المحادثات فوراً من غير ريفريش
            this.$emit('sync-conversation', {
                id: this.conversationId,
                escalated: this.escalated,
                can_open_agent: this.canOpenAgent,
                messages: this.chatMessages.map(message => ({
                    role: message.sender === 'ai' ? 'ai' : 'user',
                    text: message.text,
                    image: typeof message.image === 'string' ? message.image : null
                }))
            });

            if (data.escalated){
                this.$emit('escalate', {
                    summary: data.escalation_summary,
                    conversationId: data.conversation_id
                });
            }
        },
        scrollToBottom() {
            this.$nextTick(() => {
                const container = this.$refs.chatContainer;
                const messagesEnd = this.$refs.messagesEnd;
                if (container && messagesEnd) {
                    messagesEnd.scrollIntoView({ behavior: 'smooth', block: 'end' });
                }
            });
        },
    },
    beforeDestroy() {
        // Stop camera if running
        this.stopCamera();
        
        // Clean up object URLs to prevent memory leaks
        this.chatMessages.forEach(message => {
            if (message.image && message.image instanceof File) {
                const url = this.getImageSrc(message.image);
                if (url && url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            }
        });
        if (this.selectedImage && this.selectedImage instanceof File) {
            const url = this.getImagePreviewSrc();
            if (url && url.startsWith('blob:')) {
                URL.revokeObjectURL(url);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.chat-messages {
    min-height: 300px;
    max-height: 500px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.chat-message {
    display: flex;
    margin-bottom: 8px;

    &.user-message {
        justify-content: flex-start;

        .chat-bubble {
            background-color: #332575 !important;
            color: white !important;
            border-radius: 18px 18px 4px 18px;
            max-width: 75%;
        }
    }

    &.ai-message {
        justify-content: flex-end;

        .chat-bubble {
            background-color: rgba(0, 0, 0, 0.05);
            color: rgba(0, 0, 0, 0.87);
            border-radius: 18px 18px 18px 4px;
            max-width: 75%;
        }
    }

    .chat-bubble {
        padding: 12px 16px;
        word-wrap: break-word;
        line-height: 1.5;
    }

    .chat-text:not(.ai-response) {
        white-space: pre-wrap;
    }

    .chat-image-container {
        margin-bottom: 8px;
        
        .chat-image {
            max-width: 100%;
            max-height: 300px;
            border-radius: 8px;
            object-fit: contain;
        }
    }
}

.typing-indicator {
    display: flex;
    gap: 4px;
    padding: 8px 0;

    span {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: rgba(0, 0, 0, 0.4);
        animation: typing 1.4s infinite ease-in-out;

        &:nth-child(1) {
            animation-delay: 0s;
        }

        &:nth-child(2) {
            animation-delay: 0.2s;
        }

        &:nth-child(3) {
            animation-delay: 0.4s;
        }
    }
}

@keyframes typing {
    0%, 60%, 100% {
        transform: translateY(0);
        opacity: 0.7;
    }
    30% {
        transform: translateY(-10px);
        opacity: 1;
    }
}

.camera-container {
    position: relative;
    width: 100%;
    background-color: #000;
    min-height: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.camera-video {
    width: 100%;
    max-height: 500px;
    object-fit: contain;
}
</style>

<style lang="scss">
// Global styles for AI message formatting
.ai-response {
    .chat-html-frame > div{
        background: #fff;
    }
    .ai-heading {
        font-weight: 600;
        margin: 12px 0 8px 0;

        &.ai-h1 {
            font-size: 1.5em;
        }

        &.ai-h2 {
            font-size: 1.3em;
        }

        &.ai-h3 {
            font-size: 1.1em;
        }
    }

    .ai-bold {
        font-weight: 600;
    }

    .ai-italic {
        font-style: italic;
    }

    .ai-highlight {
        background-color: rgba(255, 235, 59, 0.3);
        padding: 2px 4px;
        border-radius: 3px;
    }

    .ai-code {
        background-color: rgba(0, 0, 0, 0.05);
        padding: 8px 12px;
        border-radius: 4px;
        font-family: 'Courier New', monospace;
        font-size: 0.9em;
        overflow-x: auto;
        margin: 8px 0;
    }

    .ai-tip {
        border-left: 3px solid var(--v-primary-base);
        padding-left: 12px;
        margin: 8px 0;
        font-style: italic;
        color: rgba(0, 0, 0, 0.7);
    }

    .ai-paragraph {
        margin: 2px 0;
        line-height: 1.6;

        &.ai-reference {
            color: rgba(0, 0, 0, 0.6);
            font-size: 0.9em;
            margin-top: 12px;
        }

        &.ai-book {
            color: rgba(0, 0, 0, 0.7);
            font-size: 0.95em;
        }

        &.ai-funny {
            color: rgba(0, 0, 0, 0.8);
            font-style: italic;
        }

        &.ai-scientific {
            color: rgba(0, 0, 0, 0.9);
            font-weight: 500;
        }

        &.ai-tip-paragraph {
            background-color: rgba(255, 235, 59, 0.1);
            padding: 8px 12px;
            border-radius: 4px;
            margin: 8px 0;
        }
    }

    .ai-arrow {
        font-weight: 600;
        color: var(--v-primary-base);
    }
    
    img {
        max-width: 100%;
        height: auto;
    }
    // .chat-logo{
    //     display: none!important;
    // }
    // p:has(.chat-logo){
    //     display: none;
    // }
}
</style>
